import { useState } from "react";
import {
  guessedImageSrc,
  imageVariantsOf,
  productImageBlur,
  productImageOriginal,
  productImageSrc,
} from "../productImage";

/**
 * A product photo as an <img>, at the size the place needs (see
 * productImage.js). Loads lazily, paints the blur preview behind itself
 * until the photo arrives, and falls back to the original if the copy
 * fails to load.
 *
 * Props
 *   product  anything with `image` (+ `image_variants`): a product, an
 *            order line, the product being edited
 *   size     'thumb' (default) | 'medium'
 *   guess    for objects that never carry `image_variants` (order lines
 *            loaded from a saved order): try the copy by its name first,
 *            and fall back to the original if it isn't there
 * Renders nothing when there's no photo; callers keep their own fallback.
 */
export default function ProductPhoto({
  product,
  size = "thumb",
  guess = false,
  alt = "",
  className,
  style,
}) {
  const original = productImageOriginal(product);
  const preferred =
    guess && !imageVariantsOf(product)
      ? guessedImageSrc(product, size)
      : productImageSrc(product, size);
  // the copy that failed, so a new product (or a new photo) tries its own
  const [failed, setFailed] = useState(null);

  if (!original) return null;
  const src = failed === preferred ? original : preferred;
  const blur = productImageBlur(product);

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      style={
        blur
          ? {
              backgroundImage: `url("${blur}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              ...style,
            }
          : style
      }
      onError={() => {
        if (src !== original) setFailed(preferred);
      }}
    />
  );
}
